<template>
  <section class="suppliers-toolbar app-filter-toolbar">
    <div class="toolbar-main-row app-filter-toolbar-row">
      <div class="search-wrap app-filter-search-wrap">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model="search"
          type="text"
          :placeholder="text.filters.searchPlaceholder"
          @input="emitSearch"
          @keyup.enter="emitApply"
        />
      </div>

      <div class="toolbar-right app-filter-toolbar-right">
        <multiselect
          v-model="filters.class"
          :options="options.class"
          track-by="value"
          label="label"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :searchable="classFilterConfig?.searchable"
          :allow-empty="true"
          :max-height="220"
          :placeholder="classFilterConfig?.placeholder"
          class="supplier-class-inline app-filter-inline-select app-filter-select app-select app-select--filters"
          @select="onSelect('class', $event)"
          @remove="onRemove('class')"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="!isOpen" class="multiselect__single">
              {{ selectionLabel('class', values) }}
            </span>
          </template>
          <template #noResult>
            <span>{{ text.common.noOptionAvailable }}</span>
          </template>
          <template #noOptions>
            <span>{{ text.common.noOptionAvailable }}</span>
          </template>
        </multiselect>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn"
          :class="{ active: showFilters }"
          :title="text.actions.toolbar.toggleFilters"
          @click="toggleFilters"
        >
          <i class="fa fa-filter" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn suppliers-toolbar-search-btn"
          :title="text.actions.toolbar.search"
          @click="emitApply"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition name="filters-expand">
      <div v-show="showFilters" class="filters-panel app-filter-panel">
        <div class="suppliers-filters-grid">
          <div
            v-for="filter in filterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ text.common.noOptionAvailable }}</span>
              </template>
              <template #noOptions>
                <span>{{ text.common.noOptionAvailable }}</span>
              </template>
            </multiselect>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import areasService from '@/services/areas';
import expenseCategoriesService from '@/services/expenseCategories';
import expenseClassesService from '@/services/expenseClasses';
import skillsService from '@/services/skills';
import { extractCollection } from '@/utils/dataNormalization';
import {
  ALL_OPTION_VALUE,
  applyRemoveOption,
  applySelectOption,
  buildSelectedValues,
  ensureDefaultSelection,
  withAllOption,
} from '@/utils/filterSelectors';
import {
  buildToolbarApplyPayload,
  buildToolbarSelectionLabel,
  createToolbarAllOption,
  mapToolbarOptions,
  resolveToolbarToggleState,
} from '@/composables/shared/useFilterToolbarShared';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('suppliers');

export default {
  name: 'SuppliersToolbar',
  emits: ['filters-change', 'search-change', 'toggle-view', 'apply'],
  computed: {
    classFilterConfig() {
      return this.filterConfigByKey('class');
    },
  },
  data() {
    const allClass = { value: ALL_OPTION_VALUE, label: text.filters.classAllLabel };
    const allCategory = createToolbarAllOption();
    const allSpecialty = createToolbarAllOption();
    const allArea = createToolbarAllOption();

    return {
      text,
      search: '',
      showFilters: false,
      options: {
        class: [allClass],
        category: [allCategory],
        specialty: [allSpecialty],
        area: [allArea],
      },
      filters: {
        class: [allClass],
        category: [allCategory],
        specialty: [allSpecialty],
        area: [allArea],
      },
      filterConfigs: [
        {
          key: 'category',
          label: text.filters.fields.category.label,
          placeholder: text.filters.fields.category.placeholder,
          searchable: true,
        },
        {
          key: 'specialty',
          label: text.filters.fields.specialty.label,
          placeholder: text.filters.fields.specialty.placeholder,
          searchable: true,
        },
        {
          key: 'area',
          label: text.filters.fields.area.label,
          placeholder: text.filters.fields.area.placeholder,
          searchable: true,
        },
      ],
    };
  },
  mounted() {
    this.loadDynamicFilters();
  },
  methods: {
    toggleFilters() {
      const nextState = resolveToolbarToggleState(this.showFilters);
      this.showFilters = nextState.showFilters;
    },
    emitSearch() {
      this.$emit('search-change', this.search);
    },
    async loadDynamicFilters() {
      const requests = [
        expenseClassesService.selectors(),
        expenseCategoriesService.selectors(),
        skillsService.selectors(),
        areasService.index(),
      ];

      const [classes, categories, specialties, areas] = await Promise.allSettled(requests);

      this.options.class = [
        { value: ALL_OPTION_VALUE, label: text.filters.classAllLabel },
        ...mapToolbarOptions(classes.status === 'fulfilled' ? extractCollection(classes.value) : []),
      ];
      this.options.category = withAllOption(mapToolbarOptions(categories.status === 'fulfilled' ? extractCollection(categories.value) : []));
      this.options.specialty = withAllOption(mapToolbarOptions(specialties.status === 'fulfilled' ? extractCollection(specialties.value) : []));
      this.options.area = withAllOption(mapToolbarOptions(areas.status === 'fulfilled' ? extractCollection(areas.value) : []));

      this.ensureAllDefault('class');
      this.ensureAllDefault('category');
      this.ensureAllDefault('specialty');
      this.ensureAllDefault('area');
    },
    ensureAllDefault(key) {
      this.filters[key] = ensureDefaultSelection(this.filters[key], this.options[key]?.[0]);
    },
    onSelect(key, option) {
      this.filters[key] = applySelectOption(this.filters[key], option, this.options[key]?.[0]);
    },
    onRemove(key) {
      this.filters[key] = applyRemoveOption(this.filters[key], this.options[key]?.[0]);
    },
    selectionLabel(key, values) {
      if (key === 'class') {
        const selected = Array.isArray(values) ? values : [];
        if (
          selected.length === 0
          || selected.some((option) => option?.value === ALL_OPTION_VALUE)
        ) {
          return buildToolbarSelectionLabel(values, text.filters.classAllLabel);
        }
      }

      return buildToolbarSelectionLabel(values);
    },
    selectedValues(key) {
      return buildSelectedValues(this.filters[key]);
    },
    filterConfigByKey(key) {
      if (key === 'class') {
        return {
          key: 'class',
          label: text.filters.fields.class.label,
          placeholder: text.filters.fields.class.placeholder,
          searchable: true,
        };
      }

      return this.filterConfigs.find((filter) => filter.key === key) || null;
    },
    emitFilters() {
      return {
        class: this.selectedValues('class'),
        category: this.selectedValues('category'),
        specialty: this.selectedValues('specialty'),
        area: this.selectedValues('area'),
      };
    },
    emitApply() {
      const nextFilters = this.emitFilters();

      this.$emit('search-change', this.search);
      this.$emit('filters-change', nextFilters);
      this.$emit('apply', buildToolbarApplyPayload(this.search, nextFilters));
    },
  },
};
</script>

<style scoped>
.supplier-class-inline {
  --app-filter-inline-width: 190px;
}

.suppliers-toolbar-search-btn {
  background-color: #0070cd;
  border-color: #0070cd;
  color: #ffffff;
}

.suppliers-toolbar-search-btn i {
  color: #ffffff;
}

.suppliers-toolbar-search-btn:hover,
.suppliers-toolbar-search-btn:focus {
  background-color: #005eae;
  border-color: #005eae;
}

.suppliers-filters-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

@media (max-width: 1200px) {
  .suppliers-filters-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .suppliers-filters-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
